import { useEffect, useRef, useState } from "react";
import { MessageCircle, Send, Paperclip, X, Car } from "lucide-react";
import { getChat, postChatMessage, markChatRead, uploadChatAttachment, getChatAttachmentData } from "../../lib/bookingChat";
import { StatusBadge, fmtDate } from "../../lib/status";
import { formatApiError } from "../../lib/api";

const POLL_MS = 20000;

/**
 * Booking-linked customer<->rental-company chat: the ONE real-time
 * conversation surface, always anchored to a single booking. `viewerType`
 * selects which of the two identically-shaped endpoint families to call
 * (`/bookings/:id/chat` for staff, `/customer/bookings/:id/chat` for the
 * renter); everything else: the message list, composer, attachment
 * handling: is shared, since the shape of the conversation is the same on
 * both sides. Deliberately NOT the ConfirmPH support system: see
 * backend/booking_chat/__init__.py for the boundary.
 */
export default function BookingChatPanel({ bookingId, viewerType }) {
  const [state, setState] = useState({ loading: true, error: null, context: null, messages: [] });
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState([]); // [{id, filename}]
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const listRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  async function load({ markAsRead = false } = {}) {
    try {
      const data = await getChat(viewerType, bookingId);
      setState({ loading: false, error: null, context: data.context, messages: data.messages });
      if (markAsRead && data.unread_count > 0) {
        await markChatRead(viewerType, bookingId);
      }
    } catch (err) {
      setState((s) => ({ ...s, loading: false, error: formatApiError(err) }));
    }
  }

  useEffect(() => {
    load({ markAsRead: true });
    const interval = setInterval(() => {
      // Don't refresh out from under someone mid-message.
      if (document.activeElement !== textareaRef.current) load({ markAsRead: true });
    }, POLL_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId, viewerType]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [state.messages]);

  async function handleAttach(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (pendingAttachments.length >= 3) return;
    setUploadingAttachment(true);
    try {
      const dataBase64 = await fileToBase64(file);
      const att = await uploadChatAttachment(viewerType, bookingId, {
        filename: file.name, contentType: file.type, dataBase64,
      });
      setPendingAttachments((a) => [...a, { id: att.id, filename: att.filename }]);
    } catch (err) {
      setState((s) => ({ ...s, error: formatApiError(err) }));
    } finally {
      setUploadingAttachment(false);
    }
  }

  async function send() {
    if (!text.trim() && pendingAttachments.length === 0) return;
    setSending(true);
    try {
      await postChatMessage(viewerType, bookingId, {
        text, attachmentIds: pendingAttachments.map((a) => a.id),
      });
      setText("");
      setPendingAttachments([]);
      await load();
    } catch (err) {
      setState((s) => ({ ...s, error: formatApiError(err) }));
    } finally {
      setSending(false);
    }
  }

  if (state.loading) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500" data-testid="booking-chat-loading">
        Loading conversation…
      </div>
    );
  }
  if (state.error && !state.context) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-rose-600" data-testid="booking-chat-error">
        Couldn't load the conversation: {state.error}
      </div>
    );
  }

  const ctx = state.context;

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid={`booking-chat-panel-${bookingId}`}>
      <div className="px-6 pt-5 pb-3 border-b border-slate-200">
        <h2 className="font-display text-lg font-semibold inline-flex items-center gap-2">
          <MessageCircle className="w-4 h-4 text-[#1B4332]" /> Conversation
        </h2>
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-600" data-testid="booking-chat-context">
          <span className="font-mono font-semibold text-slate-800">{ctx.booking_reference}</span>
          <StatusBadge status={ctx.status} />
          <span>{ctx.customer_name}</span>
          {ctx.vehicle && (
            <span className="inline-flex items-center gap-1"><Car className="w-3 h-3" /> {ctx.vehicle.name}</span>
          )}
          <span>{fmtDate(ctx.start_datetime)} – {fmtDate(ctx.end_datetime)}</span>
          {viewerType === "staff" && ctx.company && <span>· {ctx.company.name}</span>}
        </div>
      </div>

      <div ref={listRef} className="max-h-96 overflow-y-auto px-6 py-4 space-y-2.5 bg-slate-50/60" data-testid="booking-chat-messages">
        {state.messages.length === 0 && (
          <p className="text-sm text-slate-400 text-center py-6">No messages yet: start the conversation below.</p>
        )}
        {state.messages.map((m) => (
          <ChatMessage key={m.id} message={m} viewerType={viewerType} bookingId={bookingId} />
        ))}
      </div>

      {state.error && (
        <div className="px-6 py-2 text-xs text-rose-600 bg-rose-50 border-t border-rose-200">{state.error}</div>
      )}

      <div className="px-6 py-4 border-t border-slate-200">
        {pendingAttachments.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-2">
            {pendingAttachments.map((a) => (
              <span key={a.id} className="inline-flex items-center gap-1 text-xs bg-slate-100 rounded-full px-2.5 py-1" data-testid="booking-chat-pending-attachment">
                <Paperclip className="w-3 h-3" /> {a.filename}
                <button type="button" onClick={() => setPendingAttachments((list) => list.filter((x) => x.id !== a.id))} aria-label="Remove attachment">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="flex items-end gap-2">
          <textarea
            ref={textareaRef}
            data-testid={`booking-chat-composer-${bookingId}`}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            rows={2}
            maxLength={4000}
            placeholder="Write a message…"
            className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
          />
          <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                 onChange={handleAttach} data-testid="booking-chat-file-input" />
          <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploadingAttachment || pendingAttachments.length >= 3}
                  title="Attach a photo" data-testid="booking-chat-attach-btn"
                  className="min-h-11 min-w-11 inline-flex items-center justify-center rounded-lg border border-slate-300 hover:bg-slate-50 disabled:opacity-40">
            <Paperclip className="w-4 h-4" />
          </button>
          <button type="button" onClick={send} disabled={sending || (!text.trim() && pendingAttachments.length === 0)}
                  data-testid="booking-chat-send-btn"
                  className="min-h-11 inline-flex items-center gap-1.5 rounded-lg bg-[#1B4332] px-4 text-sm font-semibold text-white hover:bg-[#143027] disabled:opacity-40">
            <Send className="w-4 h-4" /> Send
          </button>
        </div>
      </div>
    </div>
  );
}

function ChatMessage({ message, viewerType, bookingId }) {
  const isMine = message.sender_type === viewerType;
  const label = message.sender_type === "staff"
    ? (message.sender_name || "Business") + (viewerType === "staff" ? "" : "")
    : (message.sender_name || "Renter");
  return (
    <div className={`flex ${isMine ? "justify-end" : "justify-start"}`} data-testid={`booking-chat-message-${message.id}`}>
      <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm ${isMine ? "bg-[#1B4332] text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"}`}>
        <div className={`text-[10px] uppercase tracking-wider mb-0.5 ${isMine ? "text-white/70" : "text-slate-500"}`}>
          {label} · {fmtDate(message.created_at)}
        </div>
        {message.text && <div className="whitespace-pre-line">{message.text}</div>}
        {message.attachments?.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {message.attachments.map((a) => (
              <AttachmentThumb key={a.id} attachment={a} viewerType={viewerType} bookingId={bookingId} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AttachmentThumb({ attachment, viewerType, bookingId }) {
  const [dataUrl, setDataUrl] = useState(null);
  useEffect(() => {
    let alive = true;
    getChatAttachmentData(viewerType, bookingId, attachment.id)
      .then((d) => { if (alive) setDataUrl(d.data_url); })
      .catch(() => {});
    return () => { alive = false; };
  }, [attachment.id, viewerType, bookingId]);
  if (!dataUrl) {
    return <div className="w-16 h-16 rounded-lg bg-black/10 animate-pulse" />;
  }
  return (
    <a href={dataUrl} target="_blank" rel="noreferrer" data-testid={`booking-chat-attachment-${attachment.id}`}>
      <img src={dataUrl} alt={attachment.filename} className="w-16 h-16 rounded-lg object-cover border border-white/30" />
    </a>
  );
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
